import ClaimFlow                  from 'scripts/defa/claim/view-claim-flow';
import ControlContainer           from 'scripts/defa/control/view-control-container';
import SettingsAutomotiveForm     from 'scripts/defa/settings-automotive/view-settings-automotive-form';
import UnitTestCollectionView     from 'scripts/defa/unit-tests/view-unit-test-collection-view';
import UnitTestFlow               from 'scripts/defa/unit-tests/view-unit-test-flow';
import UnitAlarmForm              from 'scripts/defa/units/view-unit-alarm-form';
import UnitAliasForm              from 'scripts/defa/units/view-unit-alias-form';
import UnitLocationView           from 'scripts/defa/settings-home/view-unit-location-view';
import UnitCollectionView         from 'scripts/defa/units/view-unit-collection-view';
import UnitComplaintFlow          from 'scripts/defa/units/view-unit-complaint-flow';
import UnitDeleteForm             from 'scripts/defa/units/view-unit-delete-form';
import UnitDetailsView            from 'scripts/defa/units/view-unit-details';
import UnitInfo                   from 'scripts/defa/units/view-unit-info-view.js';
import UnitNativeChoiceView       from 'scripts/defa/units/view-unit-native-choice-view';
import UnitNotesForm              from 'scripts/defa/units/view-unit-notes-form';
import UnitResetForm              from 'scripts/defa/units/view-unit-reset-form';
import UnitSearchHelpView         from 'scripts/defa/units/view-unit-search-help-view';
import UnitSearchSerialForm       from 'scripts/defa/units/view-unit-search-serial-form';
import UnitSettingsBluetoothPanel from 'scripts/defa/units/view-unit-settings-bluetooth-panel';
import UnitSettingsPanel          from 'scripts/defa/units/view-unit-settings-panel';
import UnitSnippet                from 'scripts/defa/units/view-unit-snippet';
import UnitTechnical              from 'scripts/defa/unit-technicals/model-unit-technical';
import UnitsInstalledFlow         from 'scripts/defa/units/view-units-installed-flow';
import Container                  from 'scripts/poppet/container';
import View                       from 'scripts/poppet/view';
import template                   from 'templates/unitContainer.jst.html';
import SettingsHome               from 'scripts/defa/settings-home/model-settings-home';

const channelNav    = Bb.Radio.channel( 'nav' ),
	  channelLayout = Bb.Radio.channel( 'layout' ),
	  channelUser   = Bb.Radio.channel( 'user' ),
	  channelObject = Bb.Radio.channel( 'objects' );

const selectors = {
	container     : '[data-region=unit-container]',
	info          : '[data-region=unit-info]',
	titleMyDevices: '.my-devices-title',
};

/**
 * defa.views.UnitContainer
 * A Container for all the unit related views.
 * @version 1.2.2
 */
const UnitContainer = Container.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template : template,
	className: 'unit-container',
	
	regions: {
		container: selectors.container,
		info: selectors.info,
	},
	
	templateContext: function () {
		return {
			hasDevicesTitle: this.hasDevicesTitle(),
		};
	},
	
	// ------------------------------------------------
	// Event handlers
	// ------------------------------------------------
	
	// - special functionality events
	
	/**
	 * Triggers when a child view triggers "bluetooth:scan".
	 * @void
	 */
	onChildviewBluetoothScan: function () {
		this.trigger( 'bluetooth:scan' );
	},
	
	// - snippet child events
	
	/**
	 * Triggers when a child view triggers "upgrade".
	 * @void
	 */
	onChildviewUpgrade: function ( childView ) {
		this.trigger( 'upgrade', childView.model.get( 'id' ) );
	},
	
	/**
	 * Triggers when a child view triggers "open".
	 * @void
	 */
	onChildviewOpen: function ( childView ) {
		channelNav.trigger( 'unit:control:overview', [ childView.model.get( 'id' ), ] );
	},
	
	/**
	 * Triggers when a child view triggers "settings".
	 * @void
	 */
	onChildviewSettings: function ( id ) {
		id = ( _.isNumber( id ) )
			 ? id
			 : id.model.get( 'id' );
		channelNav.trigger( 'unit:control:settings', [ id, ] );
	},
	
	/**
	 * Triggers when a child view triggers "delete".
	 * @void
	 */
	onChildviewDelete: function ( id ) {
		channelNav.trigger( 'unit:delete', [ id, ] );
	},
	
	/**
	 * Triggers when a unit is done testing.
	 * @void
	 */
	onChildviewUnitTested: function ( id ) {
		channelNav.trigger( 'unit:control:settings', [ id, ] );
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - context functions
	
	/**
	 * Checks if this view should have the devices title.
	 * @return {bool} True if this view should have the title, false if not.
	 */
	hasDevicesTitle: function () {
		return ( 'unit:list' === this.options.view && channelUser.request( 'units' ).length > 1 );
	},
	
	
	// - public methods
	
	/**
	 * Attaches a designated view into the container region.
	 * @void
	 */
	attachView: function () {
		
		this.getRegion('info').empty();
		switch ( this.options.view ) {
			
			// search-related
			
			case 'unit:nativeChoice' :
				this._showSearchNative();
				break;
			
			case 'unit:search:serial' :
				this._showSearchSerial();
				break;
			
			case 'unit:search:help' :
				this._showSearchHelp();
				break;
			
			case 'unit:list' :
				this._showUnitList();
				break;
			
			case 'unit:installed' :
				this._showInstalledUnitList();
				break;
			
			case 'unit:complaint' :
				this._showComplaintView();
				break;
			
			case 'unit:reset' :
				this._showResetForm();
				break;
			
			case 'unit:panel' :
				this._showSettingsPanel();
				break;
			
			case 'unit:delete' :
				this._showDeleteView();
				break;
			
			case 'unit:details' :
				this._showDetailsView();
				break;
			
			case 'unit:control' :
				this._showControlView();
				break;
			
			case 'unit:test' :
				this._showTestView();
				break;
			
			case 'unit:alias' :
				this._showAliasView();
				break;
			
			case 'unit:alarm' :
				if(this.options.unit.isPro()){
					this._showAlarmView();
				}else{
					channelNav.trigger( 'unit:list' );
				}
				break;
				
			case 'unit:notes' :
				this._showNotesView();
				break;
			
			case 'unit:location' :
				this._showLocationView();
				break;

			case 'unit:vehicle' :
				this._showSettingsAutomotiveForm();
				break;
			
			case 'unit:install':
				this._showInstallerView();
				break;
			
			case 'unit:testresults':
				this._showTestResultsView();
				break;
		}
		
		// hide or show the my devices title
		// !TODO: dry up this gimmick-function...
		// noinspection JSJQueryEfficiency
		if ( $( '.my-devices-title' ).hasClass( 'hidden' ) && this.hasDevicesTitle() ) {
			$( '.my-devices-title' ).removeClass( 'hidden' );
		}else{
			$( '.my-devices-title' ).addClass( 'hidden' );
		}
	},
	
	/**
	 * Checks if the current set of options matches the current units array.
	 * @return {bool} True if all requirements pass, false if not.
	 */
	checkRequirements: function () {
		
		let ret  = true,
			user = channelUser.request( 'model' ),
			family,
			role;
		
		switch ( this.options.view ) {
			
			// the panel is for tested units.
			case 'unit:control' :
				// untested units redirects to "test"
				if ( !this.options.unit.get( 'tested' ) && !user.hasRoles( [ 'TECHNICAL_SUPPORT', ] ) ) {
					channelNav.trigger( 'unit:test', [ this.options.unitId, ], { trigger: true, replace: true } );
					ret = false;
				}
				
				// if current user is an installer, and the device has no transientInstaller, redirect to "test".
				
				family = this.options.unit.get( 'family' );
				role   = 'INSTALLER_%s'.replace( '%s', family.toUpperCase() );
				
				if ( user.hasRoles( [ role, ] ) && !user.hasRoles( [ 'TECHNICAL_SUPPORT', ] ) && !this.options.unit.get( 'transientInstallerName' ) ) {
					channelNav.trigger( 'unit:test', [ this.options.unitId, ] );
					ret = false;
				}
				break;
			
			case 'unit:install' :
				
				// you cannot install a unit which has a subscription, unless you got token.
				if ( this.options.unit.get( 'transientSubscription' ) && !this.options.token ) {
					channelNav.trigger( 'unit:control:settings', [ this.options.unitId, ], { trigger: true, replace: true, } );
					ret = false;
				}
				break;
			
			case 'unit:list' :
				
				if ( this.options.units.length <= 0 ) {
					
					if ( channelLayout.request( 'is:native' ) ) {
						channelNav.trigger( 'unit:search:native', false, { trigger: true, replace: true, } );
					} else {
						channelNav.trigger( 'unit:search:serial', false, { trigger: true, replace: true, } );
					}
					ret = false;
				}
				
				if ( 1 === this.options.units.length ) {
					channelNav.trigger( 'unit:control:overview', [ this.options.units.models[ 0 ].get( 'id' ), { trigger: true, replace: true } ] );
					ret = false;
				}
				
				break;
			
			// add a case here for only installers allowed to enter complaint-mode if needed.
			
			
			// only installers have access to "installed units".
			case 'unit:installed' :
				if ( !user.hasRoles( [ 'INSTALLER_HOME', 'INSTALLER_AUTOMOTIVE', ] ) ) {
					ret = false;
				}
				break;
			
			case 'unit:reset' :
				// you must be technical support, and the unit cannot have a subscription.
				if ( !user.hasRoles( [ 'TECHNICAL_SUPPORT', ] ) || this.options.unit.get( 'transientSubscription' ) ) {
					channelNav.trigger( 'unit:control:settings', [ this.options.unitId, ], { trigger: true, replace: true, } );
					ret = false;
				}
				break;
		}
		
		return ret;
	},
	
	// - layout methods
	
	/**
	 * Displays the "Choose device type" view.
	 * @void
	 */
	_showSearchNative: function () {
		let unitNativeChoiceView = new UnitNativeChoiceView();
		this.showChildView( 'container', unitNativeChoiceView );
	},
	
	/**
	 * Displays the "Search for Unit" view.
	 * @void
	 */
	_showSearchSerial: function () {
		let unitSearchView = new UnitSearchSerialForm( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitSearchView );
	},
	
	/**
	 * Displays the "Serial number help" view.
	 * @void
	 */
	_showSearchHelp: function () {
		let unitSearchHelpView = new UnitSearchHelpView();
		this.showChildView( 'container', unitSearchHelpView );
	},
	
	/**
	 * Displays the layout for showing an installers units.
	 * @void
	 */
	_showInstalledUnitList: function () {
		let unitsInstalledFlow = new UnitsInstalledFlow( this.options );
		this.showChildView( 'container', unitsInstalledFlow );
	},
	
	/**
	 * Displays a list of Units to select from.
	 * @void
	 */
	_showUnitList: function () {
		let unitCollectionView = new UnitCollectionView( _.extend( this.options, {
			collection: this.options.units,
		} ) );
		
		// if any new units arrive async, add the getUnits UnitCollection into the unitCollectionView's UnitCollection.
		this.listenTo( this.options.user, 'change:transientUnits', function ( model ) {
			if ( !unitCollectionView.isDestroyed() ) {
				unitCollectionView.collection.add( model.getUnits().toJSON(), { merge: true } );
				unitCollectionView.render();
			}
		} );
		
		this.showChildView( 'container', unitCollectionView );
	},
	
	/**
	 * Displays the UnitSettings.
	 * @void
	 */
	_showSettingsPanel: function () {
		
		let self = this;
		
		// bluetooth units:
		if ( this.options.unit.isBluetooth() ) {
			let unitSettingsBluetoothPanel = new UnitSettingsBluetoothPanel( _.extend( this.options, {
				model: this.options.unit,
			} ) );
			self.showChildView( 'container', unitSettingsBluetoothPanel );
			
			// non-bluetooth units:
		} else {
			alert( 'Showing settings panel' ); // TODO can this method be removed altogether now?
			if ( !self.unitSettingsPanel || !self.unitSettingsPanel.isAttached() || self.unitSettingsPanel.unitId !== this.options.unitId ) {
				
				self.unitSettingsPanel = new UnitSettingsPanel( _.extend( this.options, {
					model: this.options.unit,
				} ) );
				self.showChildView( 'container', self.unitSettingsPanel );
				
			} else {
				
				self.unitSettingsPanel.settings( _.extend( this.options, {
					model: this.options.unit,
				} ) );
			}
		}
	},
	
	/**
	 * Shows the control container.
	 * @void
	 */
	_showControlView: function () {
		console.log('_showControlView');
		
		let model = this.options.unit;
		let unitSnippet;
		
		
		// if the unit already has a snippet attached to the DOM, use that snippet.
		if ( model.snippet && model.snippet.isAttached() ) {
			unitSnippet = model.snippet;
			
			// if the unit has no snippet set, create a new one.
		} else {
			unitSnippet = new UnitSnippet( _.extend( this.options, {
				state: 'expanded',
				model: model,
			} ) );
			this.showChildView( 'container', unitSnippet );
		}
		
		// if the unitSnippet already has a controlContainer, update its settings.
		if(unitSnippet.controlContainer && unitSnippet.controlContainer.isAttached()){
			unitSnippet.controlContainer.settings(this.options);
		}else{
			//  if the unitSnippet has no controlContainer create a new one.
			let controlContainer = new ControlContainer( _.extend( this.options, {
				model: model,
			} ) );
			unitSnippet.controlContainer = controlContainer;
			unitSnippet.showChildView( 'container', controlContainer );
		}
		
	},
	
	/**
	 * Displays the delete view for Units.
	 * @void
	 */
	_showDeleteView: function () {
		let unitDeleteForm = new UnitDeleteForm( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitDeleteForm );
	},
	
	_showDetailsView: function () {
		let unitDetails = new UnitDetailsView( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitDetails );
	},
	
	/**
	 * Displays the test-view for Units.
	 * @void
	 */
	_showTestView: function () {
		let unitTestFlow = new UnitTestFlow( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitTestFlow );
	},
	
	/**
	 * Shows the "Change of Unit" flow of views.
	 * @void
	 */
	_showComplaintView: function () {
		let unitComplaintFlow = new UnitComplaintFlow( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitComplaintFlow );
	},
	
	/**
	 * Shows the reset "are you sure" view.
	 * @void
	 */
	_showResetForm: function () {
		let unitResetForm = new UnitResetForm( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitResetForm );
	},
	
	/**
	 * Displays the edit alias view for this Unit.
	 * @void
	 */
	_showAliasView: function () {
		let unitAliasForm = new UnitAliasForm( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitAliasForm );
	},
	
	/**
	 * Displays the edit gerneral alarm input view for this Unit.
	 * @void
	 */
	_showAlarmView: function(){
		let unitAlarmForm = new UnitAlarmForm( _.extend( this.options, {
			model: channelObject.request('settingsHomes').get(this.options.unit.get('transientSettingsHome')) || new SettingsHome( {unit: this.options.unit.get('id')} ),
		} ) );
		this.showChildView( 'container', unitAlarmForm );
	},
	
	/**
	 * Displays the edit notes view for this Unit.
	 * @void
	 */
	_showNotesView: function () {
		let unitNotesForm = new UnitNotesForm( _.extend( this.options, {
			model: this.options.unit,
		} ) );
		this.showChildView( 'container', unitNotesForm );
	},
	
	
	/**
	 * Displays the edit location view for this Unit.
	 * @void
	 */
	_showLocationView: function () {
		let unitLocationView = new UnitLocationView( _.extend( this.options, {
			model: channelObject.request('settingsHomes').get(this.options.unit.get('transientSettingsHome')) || new SettingsHome( {unit: this.options.unit.get('id')} ),
		} ) );
		this.showChildView( 'container', unitLocationView );
	},

	/**
	 * Displays the vehicle details view for this Unit.
	 * @void
	 */
	_showSettingsAutomotiveForm: function () {
		let settingsAutomotiveForm = new SettingsAutomotiveForm( _.extend( this.options, {
			model         : this.options.settingsAutomotive,
			context       : 'vehicle',
			initValidation: false,
		} ) );
		
		// !TODO: Automate the controller to listen for the settingsAutomotive too, or end model listening from
		// controllers?
		this.listenToOnce( this.options.settingsAutomotive, 'sync', function () {
			settingsAutomotiveForm.animateOut();
		} );
		
		this.showChildView( 'container', settingsAutomotiveForm );
	},
	
	/**
	 * Shows the vehicle's test-results with unit details .
	 * @void
	 */
	_showTestResultsView: function () {
		this.listenToOnce( this.options.unit, 'tests:complete', function ( tests ) {
			let unitTestCollectionView = new UnitTestCollectionView( {
				collection: tests,
			} );
			this.showChildView( 'container', unitTestCollectionView );
		} );
		this._showUnitInfo();
		this.options.unit.testFunctionality( { newTest: false } );
	},
	
	/**
	 * Shows the claim layout when asking for an install.
	 * @void
	 */
	_showInstallerView: function () {
		let claimFlow = new ClaimFlow( _.extend( this.options, {
			model : this.options.unit,
			parent: this,
		} ) );
		this._showUnitInfo();
		this.showChildView( 'container', claimFlow );
	},
	
	_showUnitInfo: function(){
		let user = channelUser.request( 'model' );
		if (user.hasRoles( [ 'TECHNICAL_SUPPORT', ])){
			let unitTechnical        = new UnitTechnical( {
				id: this.options.unit.get( 'id' ),
			} );
			this.listenToOnce( unitTechnical, 'sync', function ( model ) {
				let unitInfoView = new UnitInfo({
					model: this.options.unit,
					tech: model,
				});
				this.showChildView('info', unitInfoView);
			} );
			unitTechnical.fetch();
		}else{
			let unitInfoView = new UnitInfo({
				model: this.options.unit,
			});
			this.showChildView('info', unitInfoView);
		}
	},
	
	/**
	 * Sets the Container's settings, and triggers a new attachView.
	 * @param {object} options  A standard set of options, usually containing both model and view.
	 * @void
	 */
	settings: function ( options ) {
		this.options = this.options || {};
		
		if ( options.unitId === this.options.unitId ) {
			_.extend( this.options, options );
		}else {
			this.options = options
		}
		
		if ( this.options.closeLink ) {
			this.closeLink = this.options.closeLink;
		}
		
		
		if ( this.options.navHandler ) {
			this.navHandler = this.options.navHandler;
		}
		
		if ( this.isRendered() && this.checkRequirements() ) {
			this.attachView();
		}
	},
} );


export default UnitContainer;
